import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState, disableIdleLockSwitch, enableIdleLockSwitch } from "../../../../globals";
import { getAccessPermissions } from "../../../../common";
import Card from "../../../../components/card";
import { BarraFiltros, BuscadorBarra, FiltroMulti, coincide } from "../../../../components/buscadorBarra/buscadorBarra";
import PeriodsService from "../../../../services/campus/periods.service";
import useEsTelefono from "../../../../utils/esTelefono";
import { ahoraCdmx, hoyCdmx } from "../../../../utils/horaMexico";
import { errorText } from "../enrollmentRequests/helpers";
import { Aviso, Campo, ConfirmarBorrado, MenuFila, Ventana } from "./piezas";

/*
	Periodos como lista de Matrículas (Horarios Claros, 2026-11-01): buscador y filtros en una barra, tabla (tarjetas en el
	teléfono), clic en la fila abre su ventana. Mismos campos y mismo servicio que la pantalla vieja de Periodos.
*/

export interface Periodo { id: number; name: string; start: string; end: string; description?: string | null; }

const SECCION = "periods";
const MS_DIA = 86400000;

const fecha = (iso: string): string => { const p = String(iso || "").substring(0, 10).split("-"); return p.length === 3 ? p[2] + "/" + p[1] + "/" + p[0] : ""; };
const aFecha = (iso: string): Date => new Date(iso.substring(0, 10) + "T12:00:00");
const anio = (p: Periodo): number => Number(String(p.start).substring(0, 4));

/** El servidor responde en inglés: lo que sí sirve de su motivo, en español. */
const motivo = (e: any, generico: string): string => {
	const m = String(e?.response?.data?.message || "");
	if (e?.response?.status === 409) return "Ya hay un periodo con ese nombre.";
	if (/out of the bounds/i.test(m)) return "No se pueden cambiar las fechas: hay horarios que quedarían fuera del periodo.";
	if (/at least a week/i.test(m)) return "Un periodo debe tener al menos 6 días entre inicio y fin.";
	if (e?.response?.status === 422 && e?.response?.data?.errors) return errorText(e);
	return generico;
};

const estadoDe = (p: Periodo, hoy: string): string => (p.start <= hoy && hoy <= p.end ? "Vigente" : p.start > hoy ? "Por Empezar" : "Terminado");
const ESTADOS = ["Vigente", "Por Empezar", "Terminado"].map((e) => ({ id: e, name: e }));
const RANGO: { [estado: string]: number } = { "Vigente": 0, "Por Empezar": 1, "Terminado": 2 };

interface Borrador { id: number | null; name: string; start: string; end: string; description: string; }

export const PeriodosTab: React.FC<{ campusId: number; onAviso: (t: string) => void; abrirNuevo: number }> = ({ campusId, onAviso, abrirNuevo }) => {
	const dispatch = useDispatch();
	const movil = useEsTelefono();
	const usuario: any = useSelector((s: RootState) => s.userProfile.value);
	const permisos: any = useMemo(() => getAccessPermissions(usuario, SECCION), [usuario]);

	const [filas, setFilas] = useState<Periodo[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [q, setQ] = useState("");
	const [anios, setAnios] = useState<number[]>([]);
	const [estados, setEstados] = useState<string[]>([]);
	const [ventana, setVentana] = useState<Borrador | null>(null);
	const [errorVentana, setErrorVentana] = useState<string | null>(null);
	const [guardando, setGuardando] = useState(false);
	const [borrando, setBorrando] = useState<Periodo | null>(null);
	const [errorBorrar, setErrorBorrar] = useState<string | null>(null);

	const cargar = useCallback(async () => {
		if (!campusId) return;
		dispatch(enableIdleLockSwitch());
		try {
			const r = await PeriodsService.searchPeriods(campusId, undefined, undefined);
			const lista: Periodo[] = Array.isArray(r.data) ? r.data : (r.data?.data ?? []);
			setFilas(lista);
			setError(null);
		} catch (e) {
			setError(motivo(e, "No se pudieron cargar los periodos."));
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	}, [campusId, dispatch]);

	useEffect(() => { setFilas(null); cargar(); }, [cargar]);

	const nuevo = useCallback(() => {
		const y = ahoraCdmx().getFullYear();
		setErrorVentana(null);
		setVentana({ id: null, name: "", start: y + "-01-01", end: y + "-12-31", description: "" });
	}, []);
	// El botón «+ Nuevo Periodo» vive en el encabezado de la pantalla: cada clic sube este contador.
	useEffect(() => { if (abrirNuevo > 0 && permisos.create) nuevo(); }, [abrirNuevo]); // eslint-disable-line react-hooks/exhaustive-deps

	const abrir = (p: Periodo) => {
		setErrorVentana(null);
		setVentana({ id: p.id, name: p.name, start: String(p.start).substring(0, 10), end: String(p.end).substring(0, 10), description: p.description || "" });
	};

	const opcionesAnio = useMemo(
		() => Array.from(new Set((filas || []).map(anio))).filter((a) => !isNaN(a)).sort((a, b) => b - a).map((a) => ({ id: a, name: String(a) })),
		[filas]
	);

	const hoy = hoyCdmx();

	// Lo que se usa primero: los vigentes, luego los que siguen (el más cercano arriba) y al final los terminados, el más reciente primero.
	const visibles = useMemo(() => (filas || [])
		.filter((p) => anios.length < 1 || anios.includes(anio(p)))
		.filter((p) => estados.length < 1 || estados.includes(estadoDe(p, hoy)))
		.filter((p) => coincide(q, p.name, p.description, fecha(p.start), fecha(p.end)))
		.sort((a, b) => {
			const ra = RANGO[estadoDe(a, hoy)], rb = RANGO[estadoDe(b, hoy)];
			if (ra !== rb) return ra - rb;
			const mas = ra === 1 ? 1 : -1;
			return a.start === b.start ? b.id - a.id : (a.start < b.start ? -mas : mas);
		}), [filas, q, anios, estados, hoy]);

	const guardar = async () => {
		if (!ventana || guardando) return;
		const nombre = ventana.name.trim();
		if (!nombre) { setErrorVentana("Escribe el nombre del periodo."); return; }
		if (!ventana.start || !ventana.end) { setErrorVentana("Elige las fechas de inicio y fin."); return; }
		const dias = (aFecha(ventana.end).getTime() - aFecha(ventana.start).getTime()) / MS_DIA;
		if (ventana.start >= ventana.end) { setErrorVentana("La fecha de inicio no puede ser mayor a la fecha final."); return; }
		if (dias < 6) { setErrorVentana("Un periodo debe tener al menos 6 días entre inicio y fin."); return; }
		setGuardando(true);
		setErrorVentana(null);
		dispatch(enableIdleLockSwitch());
		try {
			await PeriodsService.savePeriod(campusId, { id: ventana.id, name: nombre, startValue: aFecha(ventana.start), endValue: aFecha(ventana.end), description: ventana.description });
			setVentana(null);
			onAviso(ventana.id == null ? "Periodo Creado" : "Periodo Guardado");
			await cargar();
		} catch (e) {
			setErrorVentana(motivo(e, "No se pudo guardar el periodo."));
		} finally {
			setGuardando(false);
			dispatch(disableIdleLockSwitch());
		}
	};

	const borrar = async () => {
		if (!borrando) return;
		setErrorBorrar(null);
		dispatch(enableIdleLockSwitch());
		try {
			await PeriodsService.removePeriods(campusId, [borrando.id]);
			setBorrando(null);
			setVentana(null);
			onAviso("Periodo Borrado");
			await cargar();
		} catch (e) {
			setErrorBorrar(motivo(e, "No se pudo borrar: hay horarios u otra información que dependen de este periodo."));
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	const pedirBorrar = (p: Periodo) => { setErrorBorrar(null); setBorrando(p); };
	const soloLectura = ventana?.id != null && !permisos.update;
	const set = (c: Partial<Borrador>) => setVentana((v) => (v ? { ...v, ...c } : v));

	// El clic en la fila abre la ventana; el «⋯» queda para lo secundario (borrar), solo si hay permiso.
	const menu = (p: Periodo) => (permisos.delete ? [{ texto: "Borrar", onClick: () => pedirBorrar(p), peligro: true }] : []);

	const lista = () => {
		if (!filas) return <div className="loader" />;
		if (visibles.length < 1) return <div className="calVacio">Sin Periodos</div>;
		if (movil) {
			return (
				<div className="calTarjetas">
					{visibles.map((p) => {
						const est = estadoDe(p, hoy) === "Terminado" ? "" : estadoDe(p, hoy);
						return (
							<div key={p.id} className="calTarjeta" role="button" tabIndex={0} onClick={() => abrir(p)} onKeyDown={(e) => { if (e.key === "Enter") abrir(p); }}>
								<div className="calTarjetaArriba">
									<div style={{ minWidth: 0 }}><b>{p.name}</b></div>
									<div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
										{est ? <Badge bg="secondary" style={{ fontWeight: 500 }}>{est}</Badge> : null}
										<MenuFila opciones={menu(p)} />
									</div>
								</div>
								<div style={{ fontSize: 14, marginTop: 4 }}>{fecha(p.start)} - {fecha(p.end)}</div>
								{p.description ? <div className="calSub">{p.description}</div> : null}
							</div>
						);
					})}
				</div>
			);
		}
		return (
			<table className="table table-hover calLista" style={{ fontSize: 15, marginBottom: 0 }}>
				<thead>
					<tr><th>Nombre</th><th style={{ width: 120 }}>Inicio</th><th style={{ width: 120 }}>Fin</th><th style={{ width: 130 }}>Estado</th><th className="calMenu" /></tr>
				</thead>
				<tbody>
					{visibles.map((p) => {
						const est = estadoDe(p, hoy) === "Terminado" ? "" : estadoDe(p, hoy);
						return (
							<tr key={p.id} onClick={() => abrir(p)}>
								<td><b>{p.name}</b>{p.description ? <div className="calSub">{p.description}</div> : null}</td>
								<td>{fecha(p.start)}</td>
								<td>{fecha(p.end)}</td>
								<td>{est ? <Badge bg="secondary" style={{ fontWeight: 500 }}>{est}</Badge> : null}</td>
								<td className="calMenu"><MenuFila opciones={menu(p)} /></td>
							</tr>
						);
					})}
				</tbody>
			</table>
		);
	};

	return (
		<>
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}
			<Card>
				<div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<BarraFiltros hayFiltros={q !== "" || anios.length > 0 || estados.length > 0} onLimpiar={() => { setQ(""); setAnios([]); setEstados([]); }}>
						<BuscadorBarra valor={q} onBuscar={setQ} placeholder="Buscar Periodo" />
						<FiltroMulti titulo="Estado" opciones={ESTADOS} elegidos={estados} onChange={setEstados} />
						<FiltroMulti titulo="Año" opciones={opcionesAnio} elegidos={anios} onChange={setAnios} />
					</BarraFiltros>
					<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>{lista()}</div>
				</div>
			</Card>
			<div className="waSobreDock" />

			{ventana ? (
				<Ventana titulo={ventana.id == null ? "Nuevo Periodo" : "Periodo"} onClose={() => setVentana(null)} busy={guardando} pie={<>
					{ventana.id != null && permisos.delete ? (
						<button type="button" className="btn btn-outline-secondary" disabled={guardando}
							onClick={() => { const p = (filas || []).find((x) => x.id === ventana.id); if (p) pedirBorrar(p); }}>Borrar Periodo</button>
					) : null}
					<span className="calPieEspacio" />
					<button type="button" className="btn btn-outline-secondary" disabled={guardando} onClick={() => setVentana(null)}>{soloLectura ? "Cerrar" : "Cancelar"}</button>
					{soloLectura ? null : <button type="button" className="btn btn-primary" disabled={guardando} onClick={guardar}>
						<FontAwesomeIcon icon={solid("check")} />&nbsp;Guardar</button>}
				</>}>
					{errorVentana ? <Aviso>{errorVentana}</Aviso> : null}
					<form onSubmit={(e) => { e.preventDefault(); guardar(); }}>
						<div className="calCampos">
							<Campo t="Nombre" ancho>
								<input className="form-control" type="text" maxLength={50} value={ventana.name} disabled={guardando || soloLectura} autoFocus={ventana.id == null}
									onChange={(e) => set({ name: e.target.value })} />
							</Campo>
							<Campo t="Inicio">
								<input className="form-control" type="date" value={ventana.start} disabled={guardando || soloLectura} onChange={(e) => set({ start: e.target.value })} />
							</Campo>
							<Campo t="Fin">
								<input className="form-control" type="date" value={ventana.end} disabled={guardando || soloLectura} onChange={(e) => set({ end: e.target.value })} />
							</Campo>
							<Campo t="Descripción" ancho>
								<textarea className="form-control" maxLength={255} value={ventana.description} disabled={guardando || soloLectura} onChange={(e) => set({ description: e.target.value })} />
							</Campo>
						</div>
					</form>
				</Ventana>
			) : null}

			{borrando ? <ConfirmarBorrado titulo="Borrar Periodo" nombre={borrando.name} error={errorBorrar} onCancelar={() => setBorrando(null)} onBorrar={borrar} /> : null}
		</>
	);
};

export default PeriodosTab;
